/**
 * Service to aggregate and manage evidence fields for asset types
 * 
 * This service:
 * 1. Aggregates all fields from checklists associated with an asset type
 * 2. Saves aggregated fields to the asset type
 * 3. Provides fields for document processing
 */

import { assetService, AssetType } from '@/firebase/assetService';
import { checklistService, Checklist, EvidenceFieldDefinition } from '@/firebase/checklistService';
import { setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/firebase/config';

export interface AssetTypeEvidenceField {
  key: string;
  label: string;
  type: 'date' | 'string' | 'enum' | 'number' | 'boolean' | 'list';
  required: boolean;
  fileNameKey?: string;
  fileNameKeys?: string[];
  enumValues?: string[];
  workflowRules?: string;
  extractionHints?: {
    keywords?: string[];
    anchors?: string[];
    regex?: string;
  };
  normalizationRules?: {
    dateFormat?: string;
    trim?: boolean;
    uppercase?: boolean;
    lowercase?: boolean;
  };
  validationRules?: {
    min?: number;
    max?: number;
    minLength?: number;
    maxLength?: number;
    pattern?: string;
    mustBeLessThan?: string;
    mustBeGreaterThan?: string;
    customValidation?: string;
  };
  // Track which checklists/evidence definitions this field comes from
  sources?: Array<{
    checklistId: string;
    checklistName: string;
    evidenceCategoryCode: string;
  }>;
}

export interface AggregateFieldsResult {
  success: boolean;
  fieldsCount?: number;
  checklistsCount?: number;
  errors?: string[];
}

/**
 * Aggregate all evidence fields from checklists for an asset type
 */
export async function aggregateFieldsForAssetType(
  assetTypeId: string,
  organizationId: string
): Promise<AggregateFieldsResult> {
  try {
    // 1. Get the asset type
    const assetTypeResult = await assetService.getAssetType(assetTypeId);
    if (!assetTypeResult.success || !assetTypeResult.data) {
      return {
        success: false,
        errors: [`Failed to get asset type: ${assetTypeResult.error}`],
      };
    }

    // 2. Get all checklists for the organization
    const checklistsResult = await checklistService.getAllChecklists(organizationId);
    if (!checklistsResult.success || !checklistsResult.data) {
      return {
        success: false,
        errors: [`Failed to get checklists: ${checklistsResult.error}`],
      };
    }

    // 3. Filter checklists that are associated with this asset type
    // Checklists can be linked either:
    // - Directly via checklist.assetTypeIds (explicit link)
    // - Indirectly via compliance template (checklist.templateId matches assetType.complianceTemplateIds)
    const assetType = assetTypeResult.data;
    const relevantChecklists = checklistsResult.data.filter(checklist => {
      // Check direct link first
      if (checklist.assetTypeIds && checklist.assetTypeIds.length > 0) {
        return checklist.assetTypeIds.includes(assetTypeId);
      }
      
      // Check indirect link via compliance template
      if (checklist.templateId && assetType.complianceTemplateIds && assetType.complianceTemplateIds.length > 0) {
        return assetType.complianceTemplateIds.includes(checklist.templateId);
      }
      
      return false;
    });

    if (relevantChecklists.length === 0) {
      // No checklists linked to this asset type - clear fields
      await updateAssetTypeFields(assetTypeId, []);
      return {
        success: true,
        fieldsCount: 0,
        checklistsCount: 0,
      };
    }

    // 4. Aggregate all fields from evidenceDefinitions.fieldDefinitions
    const fieldsMap = new Map<string, AssetTypeEvidenceField>();

    for (const checklist of relevantChecklists) {
      if (!checklist.items || checklist.items.length === 0) continue;

      for (const item of checklist.items) {
        if (!item.evidenceDefinitions || item.evidenceDefinitions.length === 0) continue;

        for (const evidenceDef of item.evidenceDefinitions) {
          if (!evidenceDef.fieldDefinitions || evidenceDef.fieldDefinitions.length === 0) continue;

          for (const fieldDef of evidenceDef.fieldDefinitions) {
            // Check if field already exists
            if (fieldsMap.has(fieldDef.key)) {
              // Field exists - merge sources and update definition from this checklist (latest wins for type/label/etc.)
              const existingField = fieldsMap.get(fieldDef.key)!;
              if (!existingField.sources) {
                existingField.sources = [];
              }
              
              // Check if this source already exists
              const evidenceName = evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence';
              const sourceExists = existingField.sources.some(
                s => s.checklistId === checklist.id && s.evidenceCategoryCode === evidenceName
              );
              
              if (!sourceExists) {
                existingField.sources.push({
                  checklistId: checklist.id || '',
                  checklistName: checklist.name,
                  evidenceCategoryCode: evidenceName,
                });
              }
              
              // Update required status: field is required if ANY source requires it
              if (fieldDef.required || evidenceDef.required) {
                existingField.required = true;
              }
              // Update type and other definition fields so checklist edits (e.g. string -> list) are reflected after re-aggregation
              existingField.type = fieldDef.type;
              existingField.label = fieldDef.label;
              existingField.fileNameKey = fieldDef.fileNameKey;
              existingField.fileNameKeys = fieldDef.fileNameKeys;
              existingField.enumValues = fieldDef.enumValues;
              existingField.workflowRules = fieldDef.workflowRules;
              existingField.extractionHints = fieldDef.extractionHints;
              existingField.normalizationRules = fieldDef.normalizationRules;
              existingField.validationRules = fieldDef.validationRules;
            } else {
              // New field
              const aggregatedField: AssetTypeEvidenceField = {
                key: fieldDef.key,
                label: fieldDef.label,
                type: fieldDef.type,
                required: fieldDef.required || evidenceDef.required || false,
                fileNameKey: fieldDef.fileNameKey,
                fileNameKeys: fieldDef.fileNameKeys,
                enumValues: fieldDef.enumValues,
                workflowRules: fieldDef.workflowRules,
                extractionHints: fieldDef.extractionHints,
                normalizationRules: fieldDef.normalizationRules,
                validationRules: fieldDef.validationRules,
                sources: [{
                  checklistId: checklist.id || '',
                  checklistName: checklist.name,
                  evidenceCategoryCode: evidenceDef.evidenceName || (evidenceDef as any).categoryCode || 'Unknown Evidence',
                }],
              };
              
              fieldsMap.set(fieldDef.key, aggregatedField);
            }
          }
        }
      }
    }

    const aggregatedFields = Array.from(fieldsMap.values());

    // 5. Save to asset type
    await updateAssetTypeFields(assetTypeId, aggregatedFields);

    console.log(`[AggregateFieldsForAssetType] Aggregated ${aggregatedFields.length} fields from ${relevantChecklists.length} checklists for asset type ${assetTypeId}`);

    return {
      success: true,
      fieldsCount: aggregatedFields.length,
      checklistsCount: relevantChecklists.length,
    };
  } catch (error: any) {
    console.error('Error aggregating fields for asset type:', error);
    return {
      success: false,
      errors: [`Unexpected error: ${error.message}`],
    };
  }
}

/**
 * Remove undefined values from an object recursively
 */
function removeUndefined(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(removeUndefined).filter(item => item !== undefined);
  }
  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      const value = removeUndefined(obj[key]);
      if (value !== undefined) {
        cleaned[key] = value;
      }
    }
    return cleaned;
  }
  return obj;
}

/**
 * Update asset type with aggregated fields
 * Uses setDoc with merge to handle cases where the document doesn't exist yet
 */
async function updateAssetTypeFields(
  assetTypeId: string,
  fields: AssetTypeEvidenceField[]
): Promise<void> {
  const assetTypeRef = doc(db, 'asset_types', assetTypeId);
  
  // Clean fields to remove undefined values
  const cleanedFields = fields.map(field => removeUndefined(field));
  
  await setDoc(assetTypeRef, {
    evidenceFields: cleanedFields,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

/**
 * Get aggregated fields for an asset type
 */
export async function getFieldsForAssetType(
  assetTypeId: string
): Promise<{ success: boolean; fields?: AssetTypeEvidenceField[]; error?: string }> {
  try {
    const assetTypeResult = await assetService.getAssetType(assetTypeId);
    if (!assetTypeResult.success || !assetTypeResult.data) {
      return {
        success: false,
        error: assetTypeResult.error || 'Failed to get asset type',
      };
    }

    const assetType = assetTypeResult.data;
    const fields = (assetType as any).evidenceFields as AssetTypeEvidenceField[] | undefined;

    return {
      success: true,
      fields: fields || [],
    };
  } catch (error: any) {
    console.error('Error getting fields for asset type:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Re-aggregate fields for all asset types in an organization
 * Useful when checklists are updated
 */
export async function reAggregateFieldsForOrganization(
  organizationId: string
): Promise<{ success: boolean; processedCount?: number; errors?: string[] }> {
  try {
    const assetTypesResult = await assetService.getAllAssetTypes(organizationId);
    if (!assetTypesResult.success || !assetTypesResult.data) {
      return {
        success: false,
        errors: [`Failed to get asset types: ${assetTypesResult.error}`],
      };
    }

    const errors: string[] = [];
    let processedCount = 0;

    for (const assetType of assetTypesResult.data) {
      if (!assetType.id) continue;
      
      const result = await aggregateFieldsForAssetType(assetType.id, organizationId);
      if (result.success) {
        processedCount++;
      } else {
        errors.push(`Failed to aggregate fields for ${assetType.name}: ${result.errors?.join(', ')}`);
      }
    }

    return {
      success: errors.length === 0,
      processedCount,
      errors: errors.length > 0 ? errors : undefined,
    };
  } catch (error: any) {
    console.error('Error re-aggregating fields for organization:', error);
    return {
      success: false,
      errors: [`Unexpected error: ${error.message}`],
    };
  }
}

